import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from "@angular/core";
// noinspection TypeScriptPreferShortImport
import {NDropdownDirective} from "../dropdown/public_exports";

import {distinctUntilChanged} from "rxjs/operators";
import {isMobile} from "../utils";
import {NControlsValidationErrors, NInputComponent, NInputErrorMessages} from "../public_components";
import {BehaviorSubject} from "rxjs";

const NTimePickerValueAccessor = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => NTimePickerComponent),
  multi: true
};
const NTimePickerValidators = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => NTimePickerComponent),
  multi: true
};

export class NTimePickerAutocompleteItem {
  title: string;
  value: NTimePickerValue;
}

export class NTimePickerValue {
  readonly hours: number;
  readonly minutes: number;

  constructor(hours: number, minutes: number) {
    this.hours = hours;
    this.minutes = minutes;
  }

  /**
   * Creates instance from date
   */
  static createFromDate(d: Date): NTimePickerValue {
    return new NTimePickerValue(d.getHours(), d.getMinutes());
  }

  static parseNative(value: string): NTimePickerValue {
    const parts = value.split(":");
    const hour = parseInt(parts[0], 10);
    const min = parseInt(parts[1], 10);
    return new NTimePickerValue(hour, min);
  }

  /**
   * Modifiy specified date according this time
   */
  applyTo(d: Date) {
    d.setMinutes(this.minutes);
    d.setHours(this.hours);
    d.setSeconds(0);
    d.setMilliseconds(0);
  }

  greaterOrEquals(time: NTimePickerValue) {
    if (this.hours !== time.hours) {
      return this.hours >= time.hours;
    }
    return this.minutes >= time.minutes;
  }

  toNativeValue(): string {
    return `${pad(this.hours)}:${pad(this.minutes)}`;
  }
}

@Component({
  selector: "n-time-picker",
  templateUrl: "./n-time-picker.html",
  styleUrls: [
    "./n-time-picker.css"
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    NTimePickerValueAccessor,
    NTimePickerValidators,
  ],
})
export class NTimePickerComponent implements ControlValueAccessor, Validator, OnInit, OnChanges {

  @Input() name: string;
  @Input() autocompleteItems: NTimePickerAutocompleteItem[];
  @Input() minTime: NTimePickerValue;
  @Input() maxTime: NTimePickerValue;
  @Input() required: boolean;
  /**
   * Custom error message for input
   */
  @Input() errorMessages: NInputErrorMessages;
  @Output()
  blur = new EventEmitter<any>();
  @ViewChild("inputHours", {static: false}) inputHoursElement: ElementRef;
  @ViewChild("inputMinutes", {static: false}) inputMinutesElement: ElementRef;
  @ViewChild("inputError", {static: false}) inputError: NInputComponent;
  @ViewChild(NDropdownDirective, {static: false}) autocompleteDropdown: NDropdownDirective;
  // fields
  nameHour: string;
  nameMin: string;
  inFocus = false;
  inputControlError = new FormControl(null);
  inputControlMinutes = new FormControl(null);
  inputControlHours = new FormControl(null);
  inputNative = new FormControl();
  isNative: boolean;
  isError: boolean;
  withValidation: boolean;

  private hourInFocus: boolean;
  private propagateChange: (_) => void;
  private propagateTouched: () => void;
  private propagateValidation: () => void;
  private lastModelValue: NTimePickerValue;
  isEmpty$ = new BehaviorSubject(false);

  constructor(private el: ElementRef,
              private renderer: Renderer2,
              private cd: ChangeDetectorRef) {
    this.propagateValidation = () => {
    };
  }

  private _disabled: boolean;

  get disabled(): boolean {
    return this._disabled;
  }

  @Input()
  set disabled(val: boolean) { // deprecated
    this.setDisabledState(val);
  }

  @Input()
  set isDisabled(val: boolean) {
    this.setDisabledState(val);
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.inputControlHours.disable();
      this.inputControlMinutes.disable();
      this.inputNative.disable();
      this.inputControlError.disable();
    } else {
      this.inputControlHours.enable();
      this.inputControlMinutes.enable();
      this.inputNative.enable();
      this.inputControlError.enable();
    }
    this._disabled = isDisabled;
    this.refreshValidationState();
    this.cd.markForCheck();
  }

  focus() {
    this.inputMinutesElement.nativeElement.focus();
  }

  setErrors(errors: NControlsValidationErrors) {
    if (this.inputError) {
      this.inputError.setErrors(errors);
    }
  }

  ngOnInit() {

    this.isNative = isMobile();

    this.nameHour = this.name + "-hour";
    this.nameMin = this.name + "-min";

    this.inputControlHours.valueChanges
      .pipe(
        distinctUntilChanged()
      )
      .subscribe((val) => {
        this.hourChanged(val);
      });

    this.inputControlMinutes.valueChanges
      .pipe(
        distinctUntilChanged()
      )
      .subscribe((val) => {
        this.minuteChanged(val);
        this.invalidateStyles();
      });

    this.inputNative.valueChanges
      .pipe(
        distinctUntilChanged()
      )
      .subscribe((val) => {
        if (val) {
          this.assignModel(NTimePickerValue.parseNative(val));
        } else {
          this.assignModel(null);
        }
        this.invalidateNativeStyles();
      });

    this.inputControlError.statusChanges.subscribe(status => {
      this.isError = status === "INVALID";
    });
  }

  onBlur(hourField?: boolean) {
    this.inFocus = false;

    if (hourField) {
      this.hourInFocus = false;
    }

    if (this.autocompleteDropdown) {
      setTimeout(() => {
        if (!this.inFocus && !this.autocompleteDropdown.opened) {
          this.autocompleteDropdown.close();

          this.blur.emit();
          if (this.propagateTouched) {
            this.propagateTouched();
          }
        }
      }, 0);
    }

    const hour = this.inputControlHours.value;
    const min = this.inputControlMinutes.value;

    if (!hour || hour.length === 0) {
      this.inputControlHours.setValue(pad(0));
    } else if (hour && hour.length === 1) {
      this.inputControlHours.setValue(pad(parseInt(hour, 10)));
    }
    if (!min || min.length === 0) {
      this.inputControlMinutes.setValue(pad(0));
    } else if (min && min.length === 1) {
      this.inputControlMinutes.setValue(pad(parseInt(min, 10)));
    }
  }

  onFocus(hourField?: boolean) {
    this.inFocus = true;
    if (hourField) {
      this.hourInFocus = true;
    }
    if (this.autocompleteDropdown && this.autocompleteItems && this.autocompleteItems.length) {
      this.autocompleteDropdown.open();
    }
  }

  onErrorInputFocus() {
    if (!this.isNative) {
      if ((this.inputControlHours.value + "").length > 0) {
        this.inputMinutesElement.nativeElement.focus();
      } else {
        this.inputHoursElement.nativeElement.focus();
      }
    }
  }

  //  Set the function to be called when the control receives a change event.
  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }

  setTime(newTime: NTimePickerValue) {
    this.writeValue(newTime);
  }

  //  Write a new value to the element.
  writeValue(val: NTimePickerValue): void {
    this.lastModelValue = val;
    this.applyFromModel(val);
    this.invalidateStyles();

    this.fillErrorInput(val);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const {minTime, maxTime} = changes;
    const validators = [];

    if (minTime && minTime.currentValue && minTime.currentValue !== minTime.previousValue) {
      validators.push(getMinTimeNativeValidator(minTime.currentValue));
    }
    if (maxTime && maxTime.currentValue && maxTime.currentValue && maxTime.currentValue !== maxTime.previousValue) {
      validators.push(getMaxTimeNativeValidator(maxTime.currentValue));
    }

    this.setValidators(validators);
    this.refreshValidationState();
  }

  registerOnValidatorChange(fn: () => void): void {
    this.propagateValidation = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.inputControlError.errors;
  }

  private hourChanged(val: string) {
    if (val) {
      let hour = parseInt(val, 10);
      if (hour > 23) {
        hour = 23;
        this.inputControlHours.setValue(pad(hour));
      }
      if ((this.inputControlHours.value + "").length >= 2 && this.hourInFocus) {
        this.inputMinutesElement.nativeElement.focus();
      }
    }
    this.assignModelFromInputs();
  }

  private minuteChanged(val: string) {
    if (val) {
      let min = parseInt(val, 10);
      if (min > 59) {
        min = 59;
        this.inputControlMinutes.setValue(pad(min));
      }
    }
    this.assignModelFromInputs();
  }

  //  append values to form controls
  private applyFromModel(val: NTimePickerValue) {
    if (val) {
      if (typeof (val.hours) !== "undefined") {
        this.inputControlHours.setValue(pad(val.hours));
      } else {
        this.inputControlHours.setValue("");
      }
      if (typeof (val.minutes) !== "undefined") {
        this.inputControlMinutes.setValue(pad(val.minutes));
      } else {
        this.inputControlMinutes.setValue("");
      }
      this.inputNative.setValue(val.toNativeValue());
    } else {
      this.inputControlHours.setValue("");
      this.inputControlMinutes.setValue("");
      this.inputNative.setValue("");
    }
  }

  private assignModelFromInputs() {
    let hour = 0, min = 0;
    if (this.inputControlHours.value) {
      hour = parseInt(this.inputControlHours.value, 10);
    } else {
      hour = 0;
    }

    if (this.inputControlMinutes.value) {
      min = parseInt(this.inputControlMinutes.value, 10);
    } else {
      min = 0;
    }

    this.assignModel(new NTimePickerValue(hour, min));
  }

  private assignModel(value: NTimePickerValue) {
    if (this.withValidation) {
      this.fillErrorInput(value);
    }

    if (this.propagateChange) {
      this.propagateChange(value);
    }
  }

  private invalidateStyles() {
    if (!this.inputControlHours.value && !this.inputControlMinutes.value) {
      // this.renderer.addClass(this.el.nativeElement, "_empty");
      this.isEmpty$.next(true);
    } else {
      // this.renderer.removeClass(this.el.nativeElement, "_empty");
      this.isEmpty$.next(false);
    }
  }

  private invalidateNativeStyles() {
    if (!this.inputNative.value) {
      this.isEmpty$.next(true);
      // this.renderer.addClass(this.el.nativeElement, "_empty");
    } else {
      this.isEmpty$.next(false);
      // this.renderer.removeClass(this.el.nativeElement, "_empty");
    }
  }

  private fillErrorInput(value: NTimePickerValue) {
    let inputValue = "";

    if (value) {
      if (this.isNative) {
        inputValue = this.inputNative.value;
      } else {
        const hours = this.inputControlHours.value;
        const minutes = this.inputControlMinutes.value;
        inputValue = hours && minutes && (hours + "").length + (minutes + "").length >= 3 ? `${value.hours}:${value.minutes}` : "";
      }
    }

    if (inputValue !== this.inputControlError.value) {
      this.inputControlError.setValue(inputValue);
      this.propagateValidation();
    }
  }

  private setValidators(validators: ValidatorFn[]): void {
    if (this.required) {
      validators.push(Validators.required);
    }

    this.inputControlError.setValidators(validators);
    this.inputControlError.updateValueAndValidity();
    this.propagateValidation();
  }

  private refreshValidationState() {
    this.withValidation = !this.disabled && (!!this.minTime || !!this.maxTime || this.required);
  }
}

export function pad(n: number): string {
  return n < 10 ? "0" + n.toString() : n.toString();
}

const toPickerValue = (value: any) =>
  value instanceof NTimePickerValue
    ? value
    : NTimePickerValue.parseNative(value as string);

export function getMinTimeNativeValidator(minTime: NTimePickerValue): ValidatorFn {
  return (control: FormControl): ValidationErrors => {
    if (control && control.value) {
      const value = toPickerValue(control.value);
      return minTime.hours > value.hours || minTime.hours === value.hours && minTime.minutes > value.minutes
        ? {
          "time-picker-min": {
            message: `Time should be greater than ${minTime.hours}:${pad(minTime.minutes)}`,
            minTime,
          }
        }
        : null;
    }
    return null;
  };
}

export function getMaxTimeNativeValidator(maxTime: NTimePickerValue): ValidatorFn {
  return (control: FormControl): ValidationErrors => {
    if (control && control.value) {
      const value = toPickerValue(control.value);
      return maxTime.hours < value.hours || maxTime.hours === value.hours && maxTime.minutes < value.minutes
        ? {
          "time-picker-max": {
            message: `Time should be less than ${maxTime.hours}:${pad(maxTime.minutes)}`,
            maxTime,
          }
        }
        : null;
    }
    return null;
  };
}
